import { useState, useEffect, useRef, useCallback } from "react";
import { WebSocketMessage, ChatMessage } from "../types/chat";

interface UseWebSocketProps {
  url: string;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  sendMessage: (message: any) => boolean;
  sendApprovalResponse: (approved: boolean) => boolean;
  disconnect: () => void;
  reconnect: () => void;
}

export const useWebSocket = ({
  url,
  onMessage,
  onConnect,
  onDisconnect,
  onError,
}: UseWebSocketProps): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;
  const shouldConnectRef = useRef(false);

  // Store callbacks in refs to avoid dependency issues
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  // Update callback refs when props change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
  });

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;

      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close(1000, "Cleanup");
      }
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!url || !shouldConnectRef.current) {
      console.log("Connect aborted:", {
        url: !!url,
        shouldConnect: shouldConnectRef.current,
      });
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("Already connected, skipping");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log("Already connecting, skipping");
      return;
    }

    console.log("Starting WebSocket connection to:", url);
    cleanup();
    setIsConnecting(true);
    setError(null);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!shouldConnectRef.current || wsRef.current !== ws) {
          ws.close();
          return;
        }

        console.log("WebSocket connected");
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttempts.current = 0;
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        if (wsRef.current !== ws || !shouldConnectRef.current) return;

        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          onMessageRef.current?.(data);
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
          setError("Error parsing message from server");
        }
      };

      ws.onclose = (event) => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        console.log(
          "WebSocket disconnected. Code:",
          event.code,
          "Reason:",
          event.reason
        );
        setIsConnected(false);
        setIsConnecting(false);
        onDisconnectRef.current?.();

        const shouldReconnect =
          shouldConnectRef.current &&
          event.code !== 1000 &&
          event.code !== 1001 &&
          event.code !== 1006 &&
          reconnectAttempts.current < maxReconnectAttempts;

        if (shouldReconnect) {
          const timeout = Math.min(
            2000 * Math.pow(1.5, reconnectAttempts.current),
            15000
          );
          console.log(
            `Attempting to reconnect in ${timeout}ms (attempt ${
              reconnectAttempts.current + 1
            })`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldConnectRef.current) {
              reconnectAttempts.current += 1;
              connect();
            }
          }, timeout);
        } else if (event.code === 1006) {
          setError("Connection failed - server may be unreachable");
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setError("Failed to reconnect after multiple attempts");
        }
      };

      ws.onerror = (event) => {
        if (wsRef.current !== ws) return;

        console.error(
          "WebSocket error occurred. ReadyState:",
          ws.readyState,
          "URL:",
          url
        );
        setError("WebSocket connection error");
        setIsConnecting(false);
        onErrorRef.current?.(event);
      };
    } catch (err) {
      console.error("Error creating WebSocket:", err);
      setError("Failed to create WebSocket connection");
      setIsConnecting(false);
    }
  }, [url, cleanup]);

  const disconnect = useCallback(() => {
    console.log("Manually disconnecting WebSocket");
    shouldConnectRef.current = false;
    cleanup();
    setIsConnected(false);
    setIsConnecting(false);
    reconnectAttempts.current = 0;
  }, [cleanup]);

  const sendMessage = useCallback((message: any) => {
    const ws = wsRef.current;
    const readyState = ws?.readyState;

    if (ws && readyState === WebSocket.OPEN && shouldConnectRef.current) {
      try {
        ws.send(JSON.stringify(message));
        console.log("Message sent successfully:", message);
        setError(null);
        return true;
      } catch (err) {
        console.error("Error sending message:", err);
        setError("Failed to send message");
        return false;
      }
    } else {
      const states = ["CONNECTING", "OPEN", "CLOSING", "CLOSED"];
      const stateText =
        readyState !== undefined ? states[readyState] : "UNDEFINED";
      console.warn(
        `WebSocket is not ready. Exists: ${!!ws}, ReadyState: ${readyState} (${stateText}). Message not sent:`,
        message
      );

      setError("Cannot send message: not connected");
      return false;
    }
  }, []);

  const sendApprovalResponse = useCallback((approved: boolean) => {
    const message = {
      type: 'approval_response',
      approved: approved
    };
    return sendMessage(message);
  }, [sendMessage]);

  const reconnect = useCallback(() => {
    console.log("Manual reconnect requested");
    reconnectAttempts.current = 0;
    shouldConnectRef.current = true;
    cleanup();

    setTimeout(() => {
      if (shouldConnectRef.current && url) {
        connect();
      }
    }, 100);
  }, [cleanup, connect, url]);

  // Main effect - only depends on URL and connect function
  useEffect(() => {
    console.log("useEffect triggered with URL:", url);

    if (url) {
      console.log("Setting up WebSocket connection");
      shouldConnectRef.current = true;
      reconnectAttempts.current = 0;
      connect();
    } else {
      console.log("No URL provided, disconnecting");
      shouldConnectRef.current = false;
      cleanup();
      setIsConnected(false);
      setIsConnecting(false);
    }

    return () => {
      console.log("useEffect cleanup: cleaning up WebSocket");
      shouldConnectRef.current = false;
      cleanup();
      setIsConnected(false);
      setIsConnecting(false);
    };
  }, [url, connect, cleanup]);

  return {
    isConnected,
    isConnecting,
    error,
    sendMessage,
    sendApprovalResponse,
    disconnect,
    reconnect,
  };
};
